import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../utils/apiClient';

const FoundersFileManager = ({ onFoundersFileLoaded, isLoading }) => {
  const [defaultFile, setDefaultFile] = useState(null);
  const [fileLoadStatus, setFileLoadStatus] = useState('loading'); // 'loading', 'loaded', 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');
  const [showUploadMessage, setShowUploadMessage] = useState(false);
  const fileInputRef = useRef(null);
  
  const DEFAULT_FILE_PATH = '/Applications/dash/compliance-analytics/data/index/index.xlsx';
  // Оборачиваем loadDefaultFile в useCallback
  const loadDefaultFile = useCallback(async () => {
    try {
      setFileLoadStatus('loading');
      setErrorMessage('');
      
      // Запрос к серверу для получения информации о файле
      const response = await apiClient.get('/default-file-info');
      
      if (response.data && response.data.exists) {
        const fileObj = {
          name: response.data.name || 'index.xlsx',
          size: response.data.size,
          lastModified: response.data.lastModified,
          path: response.data.path || DEFAULT_FILE_PATH,
          storage: response.data.storage,
          isDefaultFile: true 
        };
        
        setDefaultFile(fileObj);
        setFileLoadStatus('loaded');
        
        if (onFoundersFileLoaded) {
          onFoundersFileLoaded(fileObj); // Передаем информацию родительскому компоненту
        }
      } else {
        setFileLoadStatus('error');
        setErrorMessage('Файл с учредителями не найден по умолчанию. Пожалуйста, загрузите новый файл.');
      }
    } catch (error) {
      console.error('Ошибка при получении информации о файле по умолчанию:', error);
      setFileLoadStatus('error');
      setErrorMessage('Не удалось получить информацию о файле. Пожалуйста, проверьте подключение к серверу.');
    }
    // Указываем зависимости useCallback (onFoundersFileLoaded важен, если он может измениться)
  }, [onFoundersFileLoaded]); 

  // Загрузка дефолтного файла при монтировании компонента
  useEffect(() => {
    loadDefaultFile();
    // Добавляем loadDefaultFile в зависимости useEffect
  }, [loadDefaultFile]); 

  // Обработчик клика по кнопке загрузки файла
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Обработчик выбора файла
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    
    if (!selectedFile) return;
    
    // Проверяем формат файла
    if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
      setErrorMessage('Пожалуйста, выберите файл Excel (.xlsx или .xls)');
      return;
    }
    
    try {
      setFileLoadStatus('loading');
      setErrorMessage('');
      setUploadMessage('Загрузка и установка файла...');
      setShowUploadMessage(true);
      
      // Создаем FormData для отправки файла
      const formData = new FormData();
      formData.append('foundersFile', selectedFile);
      
      // Отправляем файл на сервер для сохранения по указанному пути
      const response = await apiClient.post('/update-default-file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data && response.data.success) {
        const serverData = response.data;
        const resolvedLastModified = serverData.lastModified || Date.now();
        // Обновляем информацию о файле
        const fileObj = {
          name: serverData.name || selectedFile.name || 'index.xlsx',
          size: serverData.size ?? selectedFile.size,
          lastModified: resolvedLastModified,
          path: serverData.path || DEFAULT_FILE_PATH,
          storage: serverData.storage,
          isDefaultFile: true
        };

        setDefaultFile(fileObj);
        setFileLoadStatus('loaded');
        setUploadMessage(`Файл успешно обновлен (${formatLastModified(resolvedLastModified)})`);

        // Передаем информацию о файле родительскому компоненту
        if (onFoundersFileLoaded) {
          onFoundersFileLoaded(fileObj);
        }
        
        // Скрываем сообщение через 3 секунды
        setTimeout(() => {
          setShowUploadMessage(false);
        }, 3000);
      } else {
        setFileLoadStatus('error');
        setErrorMessage('Не удалось обновить файл. Пожалуйста, попробуйте еще раз.');
        setShowUploadMessage(false);
      }
    } catch (error) {
      console.error('Ошибка при обновлении файла:', error);
      setFileLoadStatus('error');
      setErrorMessage('Ошибка при обновлении файла: ' + (error.response?.data?.message || error.message));
      setShowUploadMessage(false);
    } finally {
      // Сбрасываем value у input, чтобы можно было загрузить тот же файл повторно
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Форматирование даты последнего изменения
  const formatLastModified = (timestamp) => {
    if (!timestamp) return 'Неизвестно';
    
    const date = new Date(timestamp);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="section-content">
      <div className="flex items-center justify-between mb-2">
        <h3 className="flex items-center text-base font-semibold">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
          </svg>
          Файл с учредителями
        </h3>
        
        {fileLoadStatus === 'loaded' && defaultFile && !showUploadMessage && (
          <button 
            onClick={handleUploadClick}
            disabled={isLoading}
            className="text-xs text-gray-500 flex items-center hover:text-blue-600 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
            Обновить
          </button>
        )}
      </div>
      
      {fileLoadStatus === 'loading' && !showUploadMessage && (
        <div className="flex items-center py-2 px-3 bg-blue-50 rounded text-sm">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 text-blue-500" style={{ animation: 'spin 1s linear infinite' }}>
            <line x1="12" y1="2" x2="12" y2="6"></line>
            <line x1="12" y1="18" x2="12" y2="22"></line>
            <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
            <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
            <line x1="2" y1="12" x2="6" y2="12"></line>
            <line x1="18" y1="12" x2="22" y2="12"></line>
            <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
            <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
          </svg>
          <span>Загрузка файла...</span>
        </div>
      )}
      
      {showUploadMessage && (
        <div className="flex items-center py-2 px-3 bg-green-50 rounded text-sm">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 text-green-600">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <path d="M12 18v-6"></path>
            <path d="M8 15l4 4 4-4"></path>
          </svg>
          <span>{uploadMessage}</span>
        </div>
      )}
      
      {fileLoadStatus === 'loaded' && defaultFile && !showUploadMessage && (
        <div className="flex items-center justify-between py-2 px-3 bg-green-50 rounded text-sm">
          <div className="flex items-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 text-green-600">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <span>Файл доступен</span>
          </div>
          
          <div className="text-xs text-gray-600 text-right">
            <div>Обновлен: {formatLastModified(defaultFile.lastModified)}</div>
          </div>
        </div>
      )}
      
      {fileLoadStatus === 'error' && (
        <div className="flex items-start py-2 px-3 bg-red-50 rounded text-sm text-red-700">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 mt-0.5 flex-shrink-0">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <div>
            <p className="font-medium">Ошибка: {errorMessage}</p>
            <div className="mt-2">
              <button 
                onClick={handleUploadClick}
                className="text-xs py-1 px-3 bg-red-100 hover:bg-red-200 text-red-800 rounded transition-colors"
              >
                Загрузить файл
              </button>
            </div>
          </div>
        </div>
      )}
      
      <input
        type="file"
        accept=".xlsx,.xls"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        ref={fileInputRef}
        disabled={isLoading}
      />
    </div>
  );
};

export default FoundersFileManager;
